import { z } from "zod";
import {
	defineMotionKind,
	MOTION_AT_REST,
	motionProgress,
} from "../define-motion-kind";
import { ease, lerp } from "../easing";

const emptyParamsSchema = z.object({}).strict();

/** Block in/out animations (deck blocks, docs reveal). */
export const noneAnimation = defineMotionKind({
	key: "none",
	role: "animation",
	label: "None",
	paramsSchema: emptyParamsSchema,
	defaultParams: {},
	defaultDurationMs: 0,
	gestureCapable: false,
	reducedMotionFallback: "none",
	render: () => MOTION_AT_REST,
});

/** Linear opacity, identical to the pre-registry deck FADE. */
export const fadeAnimation = defineMotionKind({
	key: "fade",
	role: "animation",
	label: "Fade",
	paramsSchema: emptyParamsSchema,
	defaultParams: {},
	defaultDurationMs: 300,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => ({ opacity: motionProgress(input), reveal: 1 }),
});

/** Linear opacity + scale from 0.92, identical to the pre-registry deck SCALE_FADE. */
export const scaleFadeAnimation = defineMotionKind({
	key: "scale-fade",
	role: "animation",
	label: "Scale and fade",
	paramsSchema: z
		.object({ fromScale: z.number().min(0.5).max(1.5) })
		.strict(),
	defaultParams: { fromScale: 0.92 },
	defaultDurationMs: 300,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = motionProgress(input);
		const scale = lerp(input.params.fromScale, 1, amount);
		return {
			opacity: amount,
			...(scale !== 1 ? { transform: `scale(${scale})` } : {}),
			reveal: 1,
		};
	},
});

export const riseAnimation = defineMotionKind({
	key: "rise",
	role: "animation",
	label: "Rise",
	paramsSchema: z.object({ distancePx: z.number().min(0).max(120) }).strict(),
	defaultParams: { distancePx: 16 },
	defaultDurationMs: 350,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(
			motionProgress(input),
			input.direction === "in" ? "enter" : "exit",
		);
		return {
			opacity: amount,
			transform: `translateY(${lerp(input.params.distancePx, 0, amount)}px)`,
			reveal: 1,
		};
	},
});

export const slideInAnimation = defineMotionKind({
	key: "slide-in",
	role: "animation",
	label: "Slide in",
	paramsSchema: z
		.object({
			from: z.enum(["left", "right"]),
			distancePx: z.number().min(0).max(240),
		})
		.strict(),
	defaultParams: { from: "left", distancePx: 40 },
	defaultDurationMs: 350,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(
			motionProgress(input),
			input.direction === "in" ? "enter" : "exit",
		);
		const sign = input.params.from === "left" ? -1 : 1;
		return {
			opacity: amount,
			transform: `translateX(${sign * lerp(input.params.distancePx, 0, amount)}px)`,
			reveal: 1,
		};
	},
});

export const blurInAnimation = defineMotionKind({
	key: "blur-in",
	role: "animation",
	label: "Blur in",
	paramsSchema: z.object({ radiusPx: z.number().min(0).max(40) }).strict(),
	defaultParams: { radiusPx: 8 },
	defaultDurationMs: 400,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(motionProgress(input), "standard");
		return {
			opacity: amount,
			filter: `blur(${lerp(input.params.radiusPx, 0, amount)}px)`,
			reveal: 1,
		};
	},
});

/**
 * The block fades as a whole; renderers with children (lists, PROCESS steps) delay child i by
 * `i * staggerMs` and play the child at the same curve.
 */
export const staggerChildrenAnimation = defineMotionKind({
	key: "stagger-children",
	role: "animation",
	label: "Stagger children",
	paramsSchema: z.object({ staggerMs: z.number().min(0).max(1000) }).strict(),
	defaultParams: { staggerMs: 80 },
	defaultDurationMs: 500,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(motionProgress(input), "enter");
		return { opacity: amount, reveal: amount };
	},
});

/** Text renderers show the first `reveal` fraction of characters; others clip left to right. */
export const typewriterAnimation = defineMotionKind({
	key: "typewriter",
	role: "animation",
	label: "Typewriter",
	paramsSchema: emptyParamsSchema,
	defaultParams: {},
	defaultDurationMs: 1200,
	gestureCapable: false,
	reducedMotionFallback: "none",
	render: (input) => {
		const reveal = motionProgress(input);
		return {
			opacity: 1,
			clipPath: `inset(0 ${(1 - reveal) * 100}% 0 0)`,
			reveal,
		};
	},
});

/** Number renderers (STATISTIC) count from `from`% of the value up to it as `reveal` runs 0→1. */
export const countUpAnimation = defineMotionKind({
	key: "count-up",
	role: "animation",
	label: "Count up",
	paramsSchema: z
		.object({ fromPercent: z.number().min(0).max(100) })
		.strict(),
	defaultParams: { fromPercent: 0 },
	defaultDurationMs: 1000,
	gestureCapable: false,
	reducedMotionFallback: "none",
	render: (input) => {
		const amount = ease(motionProgress(input), "enter");
		return { opacity: 1, reveal: amount };
	},
});

export const ANIMATION_KINDS = [
	noneAnimation,
	fadeAnimation,
	scaleFadeAnimation,
	riseAnimation,
	slideInAnimation,
	blurInAnimation,
	staggerChildrenAnimation,
	typewriterAnimation,
	countUpAnimation,
] as const;
